import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { Pagination } from "../../../components";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/tables";
import { useCustomers } from "../hooks";
import { CustomerStatus } from "../../../__generated__/globalTypes";
import { customerStatusLabel } from "../../../helpers/enum-labels";

const PAGE_SIZE = 25;

export const Prospects: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, +(searchParams.get("page") ?? 1));

  const { data, loading } = useCustomers({ where: {} });

  const visibleResults = (data?.customers?.results ?? []).filter((c) => !c.hidden);

  const sortedResults = [...visibleResults].sort((a, b) => {
    const aPriority = a.problemsDetected && a.analyseDone ? 1 : 0;
    const bPriority = b.problemsDetected && b.analyseDone ? 1 : 0;
    if (aPriority !== bPriority) return bPriority - aPriority;
    return a.name.localeCompare(b.name);
  });

  const pageCount = Math.max(1, Math.ceil(sortedResults.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pagedResults = sortedResults.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handlePageChange = (nextPage: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(nextPage));
      return next;
    });
  };

  return (
    <>
      <Header
        title="Prospects"
        subtitle="Clients actifs, problèmes détectés en priorité"
      />

      <div className="main-container">
        {!loading && !!sortedResults.length && (
          <p className="text-sm text-gray-500 dark:text-zinc-400 mb-2">
            {sortedResults.length}{" "}
            {sortedResults.length === 1 ? "résultat" : "résultats"}
          </p>
        )}

        <div className="p-4 mb-6 bg-white shadow rounded overflow-x-auto">
          {loading ? (
            <Loading />
          ) : !sortedResults.length ? (
            <EmptyList text="Aucun prospect" />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow hover={false}>
                    <TableCell header>Nom</TableCell>
                    <TableCell header>Site</TableCell>
                    <TableCell header align="center">
                      Statut
                    </TableCell>
                    <TableCell header align="center">
                      Analyse
                    </TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedResults.map((customer) => (
                    <TableRow
                      key={`prospect-${customer.id}`}
                      onClick={() => navigate(`/customer/${customer.id}`)}
                    >
                      <TableCell>{customer.name}</TableCell>
                      <TableCell className="max-w-[160px] truncate">
                        {customer.site ?? "-"}
                      </TableCell>
                      <TableCell align="center">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            customer.status === CustomerStatus.Converti
                              ? "bg-green-100 text-green-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {customerStatusLabel(customer.status)}
                        </span>
                      </TableCell>
                      <TableCell align="center">
                        <div className="flex items-center justify-center gap-1.5">
                          {customer.analyseDone && (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                              Analysé
                            </span>
                          )}
                          {customer.problemsDetected && (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              Problèmes
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Pagination page={currentPage} pageCount={pageCount} onPageChange={handlePageChange} />
            </>
          )}
        </div>
      </div>
    </>
  );
};
